import { Component, OnChanges, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';

import { Subscription } from 'rxjs';

import { geocodeService } from '@unpispas/upp-base';
import { languageService, languageSupport } from '@unpispas/upp-base';
import { toastService } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';

import { AskWaiter } from '@unpispas/upp-data';
import { dataService } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';
import { QrCodeView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';

import type { TicketPayActiveChange } from '@unpispas/feature/payment';
import { ViewRenderer } from '@unpispas/upp-wdgt';

type GuestPaymentMethod = 'PAYCASH' | 'PAYCARD';

/** Close reason for the payment panel (`back` | `paid` | `paid-online` | `pending-wait`). */
export type ToPayCloseReason = 'back' | 'paid' | 'paid-online' | 'pending-wait';

/** Close payload; optional `pendingWaitFocus` scopes WAITER to specific tickets. */
export type ToPayCloseEvent = {
    reason: ToPayCloseReason;
    pendingWaitFocus?: TicketView[];
};

/** Guest bill payment panel (home panel `PAYMENT`). */
@Component({
    selector: 'upp-guest-payment',
    templateUrl: './upp-payment.html',
    styleUrls: ['./upp-payment.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef],
    }],
})
export class UppGuestPaymentComponent extends languageSupport implements OnChanges, OnDestroy {
    @Input() qrcode: QrCodeView | null = null;
    @Output() Close = new EventEmitter<ToPayCloseEvent>();
    /** Host keeps this panel mounted (hidden) while Stripe reconcile runs in background. */
    @Output() OnlinePayChange = new EventEmitter<boolean>();

    /** Delay before auto-closing the empty payable list so the guest can read the message. */
    static readonly EMPTY_CLOSE_MS = 2000;

    /********************************/
    /* Subscriptions                */
    /********************************/

    private _qrcode_subscription: Subscription | null = null;
    private _held_subscriptions: Subscription[] = [];
    private _emptyCloseTimer: ReturnType<typeof setTimeout> | null = null;

    /** True while Stripe checkout is open and held tickets have not settled. */
    private _onlineActive = false;
    /** Tickets under online pay (held until ToPayTickets / sync catches up). */
    private _heldTickets: TicketView[] = [];
    private _pendingWaitFocus: TicketView[] = [];

    constructor(
        private lang: languageService,
        private change: ViewRenderer,
        private data: dataService,
        private toast: toastService,
        private geocode: geocodeService,
        public view: viewService,
    ) {
        super(lang, change.cdref);
    }

    ngOnChanges(): void {
        if (this._qrcode_subscription) {
            this._qrcode_subscription.unsubscribe();
            this._qrcode_subscription = null;
        }

        if (this.qrcode) {
            this._qrcode_subscription = this.qrcode.OnViewRefresh.subscribe(
            () => {
                this._SyncEmptyClose();
                this.change.markForCheck();
            });
        }

        this._SyncEmptyClose();
    }

    ngOnDestroy(): void {
        this._ClearEmptyClose();
        this._ClearHeldSubs();

        if (this._qrcode_subscription) {
            this._qrcode_subscription.unsubscribe();
            this._qrcode_subscription = null;
        }

        super.OnDestroy();
    }

    /********************************/
    /* Model accessors              */
    /********************************/

    get place(): PlaceView | null {
        return this.qrcode?.place ?? null;
    }

    get Tickets(): Array<TicketView> {
        const unpaid = this.qrcode?.UnpaidTickets ?? [];
        const topay = this.qrcode?.ToPayTickets ?? [];
        const _want = new Set<TicketView>([...unpaid, ...topay]);
        // Keep table order from `qrcode.tickets` when both lists are present.
        const _ordered = (this.qrcode?.tickets ?? []).filter((ticket) => _want.has(ticket));
        const _list = _ordered.length > 0 ? _ordered : [...unpaid, ...topay];
        // Hold Stripe tickets until ToPayTickets reflects PP (same instance).
        if (this._onlineActive && this._heldTickets.length > 0) {
            const _seen = new Set(_list);
            const _extra = this._heldTickets.filter((held) => !_seen.has(held));
            if (_extra.length > 0) {
                return [..._list, ..._extra];
            }
        }

        return _list;
    }

    get ShowTicketList(): boolean {
        const _tickets = this.Tickets;
        if (_tickets.length > 1) {
            return true;
        }

        return _tickets.length === 1 && this.IsPaymentPending(_tickets[0]);
    }

    get ShowSinglePreview(): boolean {
        const _tickets = this.Tickets;
        return _tickets.length === 1 && !this.IsPaymentPending(_tickets[0]);
    }

    /** Host must keep the Stripe CTA mounted while waiting / background reconcile. */
    get OnlineActive(): boolean {
        return this._onlineActive;
    }

    /** True when any payable ticket is awaiting confirmation (PP or Stripe in-flight). */
    get HasPendingPay(): boolean {
        return this.Tickets.some((ticket) => this.IsPaymentPending(ticket));
    }

    /** True when the diner can open the pay-options sheet (selected payable tickets). */
    get CanProceedPay(): boolean {
        return this.Selected.length > 0 && !this._onlineActive;
    }

    /** Tickets bound to the Stripe widget (selection, or held while reconciling). */
    get PayTickets(): TicketView[] {
        if (this.Selected.length > 0) {
            return this.Selected;
        }

        return this._heldTickets;
    }

    /** Amount for Stripe (same source as {@link TotalPrice}). */
    get PayAmount(): number | null {
        const _total = this.TotalPrice;
        return _total > 0 || this.PayTickets.length > 0 ? _total : null;
    }

    IsPaymentPending(ticket: TicketView): boolean {
        if (ticket.IsToPay) {
            return true;
        }

        return this._onlineActive && this._heldTickets.some((held) => held === ticket);
    }

    /**
     * Live Stripe reconcile only (`OnlineActive` + held tickets).
     * Stale `PAYLINE`/`IsToPay` without an active session is a cancellable
     * orphan — open WAITER with focus, not `onlineWait` (no cancel).
     */
    IsOnlinePaying(ticket: TicketView): boolean {
        return this._onlineActive && this._heldTickets.some((held) => held === ticket);
    }

    /** Reopens WAITER onlineWait while held tickets are still pending. */
    OnShowOnlineWait(): void {
        // Scope to the live Stripe hold only — never the whole table.
        this._pendingWaitFocus = [...this._heldTickets];
        this._EmitClose(
            'pending-wait',
            this._heldTickets.length > 0 ? this._heldTickets : undefined,
        );
        this.change.markForCheck();
    }

    /**
     * Open WAITER for pending pay — only when a single pending ticket exists.
     * Multiple pending tickets require a per-row tap (`OnPendingTicketWait`) so
     * two independent requests are never presented as one bill.
     */
    OnOpenPendingWait(): void {
        const _pending = this.Tickets.filter((ticket) => this.IsPaymentPending(ticket));
        if (_pending.length !== 1) {
            return;
        }

        this._pendingWaitFocus = [_pending[0]];
        this._EmitClose('pending-wait', [_pending[0]]);
        this.change.markForCheck();
    }

    OnPendingTicketWait(ticket: TicketView): void {
        if (!this.IsPaymentPending(ticket)) {
            return;
        }

        if (this.IsOnlinePaying(ticket)) {
            this.OnShowOnlineWait();
            return;
        }

        this._pendingWaitFocus = [ticket];
        this._EmitClose('pending-wait', [ticket]);
        this.change.markForCheck();
    }

    get PendingWaitFocus(): TicketView[] {
        return this._pendingWaitFocus;
    }

    /** Tickets held for Stripe wait UI (WAITER onlineWait). */
    get HeldOnlineTickets(): TicketView[] {
        return this._heldTickets;
    }

    /** Continuar on Stripe wait: leave WAITER; tickets stay PP until sync settles. */
    DismissOnlineWait(): void {
        this._EmitClose('back');
        this.change.markForCheck();
    }

    /********************************/
    /* Selection                    */
    /********************************/

    private _selected = new Set<TicketView>();

    get Selected(): Array<TicketView> {
        if (this.Tickets.length === 1) {
            const _only = this.Tickets[0];
            return this.IsPaymentPending(_only) ? [] : [_only];
        }

        return this.Tickets.filter(
            (ticket) => this._selected.has(ticket) && !this.IsPaymentPending(ticket),
        );
    }

    IsSelected(ticket: TicketView): boolean {
        if (this.IsPaymentPending(ticket)) {
            return false;
        }

        if (this.Tickets.length === 1) {
            return true;
        }

        return this._selected.has(ticket);
    }

    OnToggleSelect(ticket: TicketView): void {
        if (this.IsPaymentPending(ticket)) {
            return;
        }

        if (this._selected.has(ticket)) {
            this._selected.delete(ticket);
        }
        else {
            this._selected.add(ticket);
        }

        this.change.markForCheck();
    }

    OnSelectChange(ticket: TicketView, checked: boolean): void {
        if (checked === this._selected.has(ticket)) {
            return;
        }

        this.OnToggleSelect(ticket);
    }

    TicketTrack(_index: number, ticket: TicketView): any {
        return ticket?._uuid || null;
    }

    /********************************/
    /* Amounts and elapsed          */
    /********************************/

    get TotalPrice(): number {
        return this.PayTickets.reduce((total, ticket) => total + (ticket.due || 0), 0);
    }

    get TotalPriceInfo(): { int: string; dec: string } {
        return this.PriceParts(this.TotalPrice);
    }

    PriceParts(value: number): { int: string; dec: string } {
        const _price = Math.round(value * 100) / 100;
        const _parts = Math.abs(_price).toString().split('.');

        return {
            int: _parts[0],
            dec: (_parts.length > 1) ? (_parts[1] + '0').slice(0, 2) : '00',
        };
    }

    TicketElapsed(ticket: TicketView): string {
        const _created = ticket.created;
        if (!_created) {
            return this.tr('@created_time_just_now');
        }

        const _minutes = Math.round((Date.now() - _created.getTime()) / 60000);
        if (_minutes < 1) {
            return this.tr('@created_time_just_now');
        }

        if (_minutes < 60) {
            return this.tr('@created_time_minutes', [_minutes]);
        }

        return this.tr('@created_time_hours', [Math.floor(_minutes / 60), _minutes % 60]);
    }

    /********************************/
    /* Ticket preview (list overlay)*/
    /********************************/

    private _showticket: TicketView | null = null;

    get ShowTicket(): TicketView | null {
        return this._showticket;
    }

    /** Opens shared receipt preview from the multi-ticket list; `null` dismisses. */
    OnShowTicket(ticket: TicketView | null): void {
        this._showticket = ticket;
        this.change.markForCheck();
    }

    /********************************/
    /* Payment options              */
    /********************************/

    private _showpayment = false;

    get ShowPayment(): boolean {
        return this._showpayment;
    }

    OnShowPayment(): void {
        if (this.Selected.length === 0) {
            return;
        }

        this._showpayment = true;
        this.change.markForCheck();
    }

    OnHidePayment(): void {
        if (this._onlineActive) {
            return;
        }

        this._showpayment = false;
        this.change.markForCheck();
    }

    /** Checkout opened — hold tickets and open WAITER; settle follows ticket sync. */
    OnOnlineActiveChange(event: TicketPayActiveChange): void {
        if (!event.active) {
            return;
        }

        if (event.tickets.some((ticket) => ticket.ToInvoice)) {
            return;
        }

        this._onlineActive = true;
        this._heldTickets = [...event.tickets];
        this._pendingWaitFocus = [...event.tickets];
        this._showpayment = false;
        this._ClearEmptyClose();
        this._BindHeldSettle();
        this.OnlinePayChange.emit(true);
        // Scope WAITER to the checkout tickets only (same set as Stripe held).
        this._EmitClose('pending-wait', event.tickets);
        this.change.markForCheck();
    }

    /********************************/
    /* Online settle (ticket model) */
    /********************************/

    private _BindHeldSettle(): void {
        this._ClearHeldSubs();
        for (const _ticket of this._heldTickets) {
            this._held_subscriptions.push(
                _ticket.OnViewRefresh.subscribe(() => {
                    this._CheckHeldSettled();
                })
            );
        }
    }

    private _ClearHeldSubs(): void {
        for (const _sub of this._held_subscriptions) {
            _sub.unsubscribe();
        }
        this._held_subscriptions = [];
    }

    /**
     * Ticket is source of truth: PD/IsPaid → receipt; released unpaid → end session.
     * One checkout settles the held set together.
     */
    private _CheckHeldSettled(): void {
        if (!this._onlineActive || this._heldTickets.length === 0) {
            return;
        }

        if (this._heldTickets.every((ticket) => ticket.IsPaid)) {
            this._EndOnlineSession();
            this.toast.ShowAlert('primary', this.tr('@pay_ticket_confirmed'), 2500);
            this._EmitClose('paid-online');
            return;
        }

        if (this._heldTickets.every((ticket) => !ticket.IsPaid && !ticket.IsToPay)) {
            this._EndOnlineSession();
            this.toast.ShowAlert('danger', this.tr('@pay_ticket_failed'), 3000);
        }
    }

    private _EndOnlineSession(): void {
        this._ClearHeldSubs();
        this._onlineActive = false;
        this._heldTickets = [];
        this.OnlinePayChange.emit(false);
        this._SyncEmptyClose();
        this.change.markForCheck();
    }

    get OnlyOnlinePay(): boolean {
        return this.place?.GetConfigOption('optionQrOnlyOnline', false) ?? false;
    }

    get CanCash(): boolean {
        return !!this.place?.TableService;
    }

    get CanCard(): boolean {
        return !!this.place?.CanCard && (this.TotalPrice >= (this.place?.CardMinimum ?? 0));
    }

    /** Selected sale needs a full invoice at the till (amount limit or 0% VAT). */
    get RequiresTillInvoice(): boolean {
        return this.Selected.some((ticket) => ticket.ToInvoice);
    }

    /** In-site payment stays available when the sale cannot be paid online. */
    get ShowInSiteOptions(): boolean {
        return !this.OnlyOnlinePay || this.RequiresTillInvoice;
    }

    get ShowCashOption(): boolean {
        return this.CanCash && this.ShowInSiteOptions;
    }

    get ShowCardOption(): boolean {
        return this.CanCard && this.ShowInSiteOptions;
    }

    get ShowOnlineOption(): boolean {
        return !!this.place?.CanLine && !this.RequiresTillInvoice;
    }

    /** Ask the waiter when the sale needs an invoice and the place has no in-site method. */
    get ShowInvoiceOption(): boolean {
        return this.RequiresTillInvoice && !this.ShowCashOption && !this.ShowCardOption;
    }

    get NoPaymentMethods(): boolean {
        return !this.ShowCashOption && !this.ShowCardOption && !this.ShowOnlineOption && !this.ShowInvoiceOption;
    }

    /********************************/
    /* Close                        */
    /********************************/

    OnClose(): void {
        this._ClearEmptyClose();
        this._EmitClose('back');
    }

    /** Emits {@link Close} with an optional single-ticket WAITER focus. */
    private _EmitClose(reason: ToPayCloseReason, pendingWaitFocus?: TicketView[]): void {
        this.Close.emit(
            pendingWaitFocus && pendingWaitFocus.length > 0
                ? { reason, pendingWaitFocus }
                : { reason },
        );
    }

    private _SyncEmptyClose(): void {
        if (this._onlineActive) {
            this._ClearEmptyClose();
            return;
        }

        if (this.Tickets.length === 0) {
            this._ArmEmptyClose();
            return;
        }

        this._ClearEmptyClose();
    }

    private _ArmEmptyClose(): void {
        if (this._emptyCloseTimer) {
            return;
        }

        this._emptyCloseTimer = setTimeout(() => {
            this._emptyCloseTimer = null;
            this.OnClose();
        }, UppGuestPaymentComponent.EMPTY_CLOSE_MS);
    }

    private _ClearEmptyClose(): void {
        if (!this._emptyCloseTimer) {
            return;
        }

        clearTimeout(this._emptyCloseTimer);
        this._emptyCloseTimer = null;
    }

    /********************************/
    /* Charge + waiter              */
    /********************************/

    private _waitmodal = false;
    PayCashTickets: TicketView[] | null = null;
    PayCardTickets: TicketView[] | null = null;
    private _payCashResolve: ((ok: boolean) => void) | null = null;
    private _payCardResolve: ((ok: boolean) => void) | null = null;

    async OnPay(payment: GuestPaymentMethod): Promise<void> {
        const _payTickets = [...this.Selected];
        if (_payTickets.length === 0) {
            return;
        }

        const _nearby = !this.place?.TableService
            || this.place.QrGeolocation === false
            || await this.geocode.NearBy(this.place?.Coordinates ?? null);
        if (!_nearby) {
            this.change.markForCheck();
            return;
        }

        const _dopay = (payment === 'PAYCASH')
            ? await this.OnPaycash(_payTickets)
            : await this.OnPaycard(_payTickets);
        if (!_dopay) {
            this.change.markForCheck();
            return;
        }

        const _transaction = this.data.transaction;
        let _sentprepay = false;

        for (const ticket of _payTickets) {
            _sentprepay = _sentprepay || ticket.IsPrepay;

            const _waiter = new AskWaiter(null, this.data);
            _waiter.ticket = ticket.object;
            _waiter.session = this.data.session ?? null;
            _waiter.reason = payment;
            _waiter.amount = ticket.given || 0;
            _waiter.status = 'AC';

            ticket.request = _waiter;
            await ticket.DoPayment(payment);

            await _transaction.push(ticket.object);
            await _transaction.push(_waiter);
        }

        await _transaction.flush();

        if (_sentprepay) {
            this.toast.ShowAlert('primary', this.tr('@place_ticket_sent_toast'), 2000);
        }

        // Scope WAITER to the tickets just paid — not every PP row on the table.
        this._EmitClose('paid', _payTickets);
        this.change.markForCheck();
    }

    private OnPaycash(tickets: TicketView[]): Promise<boolean> {
        if (!this.place?.CashModal) {
            return Promise.resolve(true);
        }

        if (this._waitmodal) {
            return Promise.resolve(false);
        }

        this._waitmodal = true;
        this.PayCashTickets = tickets;
        this.change.markForCheck();
        return new Promise((resolve) => {
            this._payCashResolve = resolve;
        });
    }

    OnPayCashAccept(result: { amount: number | null }): void {
        const tickets = this.PayCashTickets ?? [];
        if (result && tickets.length > 0) {
            let _remaining = Number(result.amount ?? 0);
            for (const ticket of tickets) {
                const given = ticket.price || 0;
                ticket.given = given;
                _remaining -= given;
            }

            _remaining += tickets[0].given || 0;
            tickets[0].given = Math.round(_remaining * 100) / 100;
        }

        this._FinishPayCash(result != null);
    }

    OnPayCashClose(): void {
        this._FinishPayCash(false);
    }

    private _FinishPayCash(ok: boolean): void {
        const resolve = this._payCashResolve;
        this._payCashResolve = null;
        this.PayCashTickets = null;
        this._waitmodal = false;
        this.change.markForCheck();
        resolve?.(ok);
    }

    private OnPaycard(tickets: TicketView[]): Promise<boolean> {
        if (!this.place?.CardModal) {
            return Promise.resolve(true);
        }

        if (this._waitmodal) {
            return Promise.resolve(false);
        }

        this._waitmodal = true;
        this.PayCardTickets = tickets;
        this.change.markForCheck();
        return new Promise((resolve) => {
            this._payCardResolve = resolve;
        });
    }

    OnPayCardAccept(): void {
        this._FinishPayCard(true);
    }

    OnPayCardClose(): void {
        this._FinishPayCard(false);
    }

    private _FinishPayCard(ok: boolean): void {
        const resolve = this._payCardResolve;
        this._payCardResolve = null;
        this.PayCardTickets = null;
        this._waitmodal = false;
        this.change.markForCheck();
        resolve?.(ok);
    }
}
